<template>
  <div class="app-table" :class="{ 'app-table--cards': cards }">
    <!-- Телефон (режим cards): сортировка — выпадающий список вместо шапки таблицы -->
    <div v-if="cards && sortableColumns.length" class="app-table__sortbar">
      <select class="app-table__sortselect" :value="sortKey" aria-label="Сортировка" @change="$emit('sort', $event.target.value)">
        <option v-if="!sortableColumns.some((c) => c.key === sortKey)" value="" disabled>Сортировка…</option>
        <option v-for="c in sortableColumns" :key="c.key" :value="c.key">{{ c.label }}</option>
      </select>
      <button type="button" class="app-table__sortdir" :disabled="!sortKey" :title="sortDir === 'asc' ? 'По возрастанию' : 'По убыванию'" @click="sortKey && $emit('sort', sortKey)">
        <i class="fas" :class="sortDir === 'asc' ? 'fa-sort-amount-up-alt' : 'fa-sort-amount-down-alt'"></i>
      </button>
    </div>
    <div class="app-table__scroll">
      <table class="app-table__grid">
        <thead>
          <tr>
            <th
              v-for="(col, idx) in columns"
              :key="col.key || idx"
              :style="thStyle(col)"
              :class="{ 'is-sortable': col.sortable, 'is-sorted': col.sortable && col.key === sortKey, 'is-sticky-right': isSticky(col) }"
              :title="!col.headerButton && col.headerIcon ? col.label : null"
              :aria-sort="ariaSort(col)"
              @click="col.sortable && $emit('sort', col.key)"
            >
              <app-button
                v-if="col.headerButton"
                tag="router-link"
                :to="col.headerButton.to"
                variant="primary"
                size="sm"
                :icon="col.headerButton.icon"
                :title="col.headerButton.title || col.label"
              />
              <i v-else-if="col.headerIcon" :class="col.headerIcon"></i>
              <template v-else>
                {{ col.label }}
                <i
                  v-if="col.sortable"
                  class="app-table__sort fas"
                  :class="col.key === sortKey ? (sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'"
                ></i>
              </template>
            </th>
          </tr>
        </thead>

        <tbody>
          <!-- Пустое состояние -->
          <tr v-if="!rows.length">
            <td :colspan="columns.length">
              <div class="app-table__empty">
                <slot name="empty">
                  <i class="fas fa-route"></i>
                  <h3>Нет данных</h3>
                  <p>Тут пока пусто</p>
                </slot>
              </div>
            </td>
          </tr>

          <!-- Строки -->
          <tr
            v-for="(row, rowIdx) in rows"
            :key="resolveKey(row, rowIdx)"
            :class="[rowClass(row, rowIdx), { 'is-clickable': rowClickable }]"
            @click="rowClickable && $emit('row-click', row)"
          >
            <td
              v-for="(col, colIdx) in columns"
              :key="col.key || colIdx"
              :style="tdStyle(col)"
              :class="{ 'is-sticky-right': isSticky(col) }"
              :data-label="col.label || null"
            >
              <!-- Содержимое ячейки одним блоком: в карточках (телефон) ячейка — «подпись + значение», и несколько элементов значения не должны встать в ряд -->
              <div class="app-table__cell">
                <template v-if="hasSlot(col.key)">
                  <slot :name="col.key" :row="row" :index="rowIdx" />
                </template>
                <template v-else>
                  <slot v-if="col.slot === 'default'" :row="row" :index="rowIdx"></slot>
                  <template v-else-if="col.format">{{ formatCell(row, col) }}</template>
                  <span v-else>{{ row[col.key] }}</span>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppTable',
  props: {
    columns: { type: Array, required: true }, // [{ key, label, width, minWidth, align, format, slot, headerIcon, headerButton }]
    // headerIcon (класс FA) — если задан, в шапке колонки вместо текста label
    // показывается только иконка (label уходит в title для подсказки при наведении).
    // headerButton ({ icon, to, title? }) — кликабельная кнопка-ссылка в шапке
    // колонки (например "+" на страницу добавления) вместо текста label;
    // приоритетнее headerIcon, если заданы оба.
    // Для узких служебных колонок (например "Действия"), где текст занимает
    // непропорционально много места.
    // Сортировка (необязательно): у колонки { sortable: true } заголовок кликабелен и
    // шлёт событие sort с key колонки. Сами строки сортирует родитель; sortKey и sortDir
    // ('asc' | 'desc') нужны только для стрелки в шапке.
    sortKey: { type: String, default: '' },
    sortDir: { type: String, default: 'asc' },
    rows: { type: Array, default: () => [] },
    rowKey: { type: [Function, String], default: (row, i) => i },
    rowClass: { type: Function, default: () => ({}) },
    // Строка целиком нажимается: клик по ней шлёт событие row-click (клики по вложенным кнопкам и ссылкам глушатся самими ячейками)
    rowClickable: { type: Boolean, default: false },
    // На телефоне (до 768 px) строки показываются карточками: у каждой ячейки подпись колонки, сортировка — списком над карточками.
    // Нужна страницам, где таблица шире экрана; страницы с собственной вёрсткой для телефона её не включают.
    cards: { type: Boolean, default: false }
  },
  computed: {
    sortableColumns() {
      return this.columns.filter((c) => c.sortable && c.key)
    }
  },
  methods: {
    ariaSort(col) {
      if (!col.sortable) return null
      if (col.key !== this.sortKey) return 'none'
      return this.sortDir === 'asc' ? 'ascending' : 'descending'
    },
    resolveKey(row, index) {
      // rowKey может быть функцией или строкой (имя поля-ключа)
      if (typeof this.rowKey === 'function') return this.rowKey(row, index)
      if (typeof this.rowKey === 'string' && this.rowKey) return row[this.rowKey]
      return index
    },
    hasSlot(key) {
      return !!(key && this.$scopedSlots[key])
    },
    // Колонка действий закреплена справа и видна при горизонтальной прокрутке (key 'actions' или sticky: 'right').
    isSticky(col) {
      return col.sticky === 'right' || (col.sticky !== false && col.key === 'actions')
    },
    thStyle(col) {
      const s = {}
      if (col.width) s.width = col.width
      if (col.minWidth) s.minWidth = col.minWidth
      if (col.align) s.textAlign = col.align
      return s
    },
    tdStyle(col) {
      const s = {}
      if (col.align) s.textAlign = col.align
      return s
    },
    formatCell(row, col) {
      if (typeof col.format === 'function') return col.format(row[col.key], row)
      return row[col.key]
    }
  }
}
</script>

<style scoped>
.app-table {
  flex: 1;
  min-height: 0;
  background: var(--bg-surface);
  border-radius: var(--radius-xl);
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
}

.app-table__cell { display: contents; }
.app-table__scroll {
  flex: 1;
  overflow: auto;
}

.app-table__grid {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
}

tr.is-clickable { cursor: pointer; }
tr.is-clickable:active td { background: var(--bg-subtle); }

thead {
  background: var(--bg-surface-muted);
  border-bottom: var(--border-width-default) var(--border-style-base) var(--border-strong);
  box-shadow: 0 1px 0 var(--border-default), 0 2px 6px -4px rgba(15, 23, 42, 0.18);
  position: sticky;
  top: 0;
  z-index: 10;
}

th {
  padding: var(--space-5) var(--space-8);
  text-align: left;
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wide);
  white-space: nowrap;
}

th.is-sortable {
  cursor: pointer;
  user-select: none;
}
th {
  transition: color var(--duration-fast) var(--ease-base);
}
th.is-sortable:hover,
th.is-sorted {
  color: var(--primary);
}
.app-table__sort {
  margin-left: var(--space-2);
  font-size: var(--text-2xs);
  opacity: 0.45;
}
th.is-sorted .app-table__sort,
th.is-sortable:hover .app-table__sort {
  opacity: 1;
}

td {
  padding: var(--space-5) var(--space-8);
  border-bottom: var(--border-width-default) var(--border-style-base) var(--color-neutral-100);
  color: var(--text-primary);
  vertical-align: middle;
}

tbody tr:hover td {
  background: var(--bg-surface-muted);
}

/* Закреплённая справа колонка: непрозрачный фон и тонкая граница слева, чтобы не просвечивали уходящие под неё ячейки */
th.is-sticky-right,
td.is-sticky-right {
  position: sticky;
  right: 0;
  box-shadow: -1px 0 0 var(--border-default);
}
th.is-sticky-right {
  z-index: 2;
  background: var(--bg-surface-muted);
}
td.is-sticky-right {
  z-index: 1;
  background: var(--bg-surface);
}
tbody tr:hover td.is-sticky-right {
  background: var(--bg-surface-muted);
}

tbody tr:last-child td {
  border-bottom: none;
}

.app-table__empty {
  text-align: center;
  padding: var(--space-24) var(--space-10);
  color: var(--text-tertiary);
}
.app-table__empty i {
  font-size: var(--text-2xl);
  color: var(--color-neutral-300);
  margin-bottom: var(--space-8);
  display: block;
}
.app-table__empty h3 {
  margin: 0 0 var(--space-2);
  font-size: var(--text-lg);
  color: var(--text-secondary);
  font-weight: var(--font-weight-semibold);
}
.app-table__empty p {
  margin: 0;
  font-size: var(--text-sm);
}

.app-table__sortbar { display: none; }

/* Телефон и планшет в портрете: таблица растёт по содержимому (страница прокручивается), вбок — свой скролл. */
@media (max-width: 900px) {
  .app-table {
    flex: none;
    overflow: visible;
    min-height: 0;
  }
  .app-table__scroll {
    flex: none;
    overflow-x: auto;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
    border-radius: var(--radius-xl);
  }
  thead { position: static; }
  th,
  td {
    padding: var(--space-5) var(--space-6);
  }
}

/* Режим cards на телефоне: каждая строка — карточка, у ячейки слева подпись колонки (data-label) */
@media (max-width: 768px) {
  .app-table--cards {
    background: transparent;
    border: 0;
    box-shadow: none;
    border-radius: 0;
  }
  .app-table--cards .app-table__sortbar {
    display: flex;
    gap: var(--space-4);
    margin-bottom: var(--space-6);
  }
  .app-table__sortselect {
    flex: 1;
    min-width: 0;
    padding: var(--space-4) var(--space-6);
    font: inherit;
    color: var(--text-primary);
    background: var(--bg-surface);
    border: var(--border-width-default) solid var(--border-default);
    border-radius: var(--radius-md);
  }
  .app-table__sortdir {
    flex-shrink: 0;
    width: 44px;
    color: var(--primary);
    background: var(--bg-surface);
    border: var(--border-width-default) solid var(--border-default);
    border-radius: var(--radius-md);
    cursor: pointer;
  }
  .app-table__sortdir:disabled { color: var(--text-tertiary); cursor: default; }
  .app-table--cards .app-table__scroll { overflow: visible; border-radius: 0; }
  .app-table--cards thead { display: none; }
  .app-table--cards .app-table__grid,
  .app-table--cards tbody,
  .app-table--cards tr,
  .app-table--cards td { display: block; width: 100%; box-sizing: border-box; }
  .app-table--cards tbody tr {
    margin-bottom: var(--space-6);
    padding: var(--space-3) 0;
    background: var(--bg-surface);
    border: var(--border-width-default) solid var(--border-default);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm);
    overflow: hidden;
  }
  .app-table--cards td {
    display: flex;
    align-items: baseline;
    gap: var(--space-6);
    padding: var(--space-3) var(--space-8);
    border: 0;
    text-align: left !important;
    overflow-wrap: anywhere;
  }
  .app-table--cards td::before {
    content: attr(data-label);
    flex: 0 0 34%;
    max-width: 34%;
    font-size: var(--text-xs);
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: var(--letter-spacing-wide);
  }
  .app-table--cards td:not([data-label])::before { content: none; }
  /* значение занимает остаток строки и переносится, а не сжимается в колонку букв */
  .app-table--cards .app-table__cell { display: block; flex: 1 1 0; min-width: 0; }
  .app-table--cards td:has(> .app-table__cell:empty) { display: none; }
  .app-table--cards td.is-sticky-right { position: static; box-shadow: none; background: transparent; }
  .app-table--cards tbody tr:hover td { background: transparent; }
  .app-table--cards td[colspan] { display: block; padding: 0; }
}
</style>
